/**
 * FrontLot — government form field mappings
 *
 * Field names below were read directly out of the PDFs, not guessed. They are
 * verbatim, including the inconsistent spacing in names like "100, 000 Place".
 * If a name looks wrong, check form_field_inventory.json before "fixing" it.
 *
 * Checkbox export values are also verbatim. 82040's residency/citizenship
 * radios use /YES, /YES_2, /YES_4, /YES_5 — not /Yes and not /On. Setting the
 * wrong value fails silently and prints a blank box.
 *
 * Deliberately left blank, confirmed with the dealer:
 *   - 82040 "Are you a US Citizen" — not collected, so never set.
 * Do not "complete" these later without asking.
 */

import { makeCode, colorCode, bodyCode } from "./codes";
import { warrantySummary } from "./warrantyPage";

// ---------------------------------------------------------------------------
// Context: what the app hands the form layer
// ---------------------------------------------------------------------------

/**
 * Dates as they are written on Florida paperwork: 09-11-2026.
 *
 * The database stores ISO (2026-09-11), which sorts correctly and cannot be
 * misread by a computer — but on a title application it reads as a typo, and
 * a clerk at the tax collector expects month first. Every date that reaches a
 * form goes through here, so no form can print the other way.
 */
export function usDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[2]}-${m[3]}-${m[1]}` : iso;
}

export interface DealerSettings {
  legalName: string;
  dba: string | null;
  flhsmvLicenseNo: string;
  salesTaxRegNo: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  phone: string;   // 10 digits
  email: string;
}

export interface Person {
  firstName: string;
  middleName: string | null;
  lastName: string;
  suffix: string | null;
  dateOfBirth: string;          // ISO yyyy-mm-dd
  idNumber: string;
  /* A business buyer. The company is the applicant; the person signs. */
  isBusiness?: boolean;
  businessName?: string | null;
  businessEin?: string | null;
  businessTitle?: string | null;
  idState: string;
  street: string;
  city: string;
  county: string | null;
  state: string;
  zip: string;
  mailSameAsPhysical: boolean;
  mailStreet: string | null;
  mailCity: string | null;
  mailState: string | null;
  mailZip: string | null;
  phoneCell: string | null;
  email: string | null;
}

export interface Vehicle {
  vin: string;
  askingPriceCents?: number;
  year: number;
  make: string;
  model: string;
  bodyType: string | null;
  exteriorColor: string | null;
  odometer: number;
  weight: string | null;
  titleNumber: string | null;
  titleState: string | null;
  titleBrand: string | null;
}

export interface TradeIn {
  vin: string;
  year: number;
  make: string;
  titleNumber: string | null;
}

export interface Deal {
  dealNo: string;
  agreementDate: string;        // ISO
  cashPriceCents: number;
  stateTaxCents: number;
  surtaxCents: number;
  /** How two buyers hold the title: both must sign to sell, or either may. */
  jointOwnership?: "and" | "or" | null;
  /** "transfer" when the buyer brings a plate, "new" when one is issued. */
  tagChoice?: "transfer" | "new";
  /** The plate being transferred, when the buyer has the number to hand. */
  tagNumber?: string | null;
}

/**
 * Every line of the itemization, straight from the database's generated
 * columns. Nothing here is recomputed, so the contract and the deal screen
 * cannot disagree.
 */
export interface DealAmountsCents {
  cashPrice: number; predelivery: number; efiling: number;
  tradeAllowance: number; taxable: number; stateTax: number; surtax: number;
  totalSales: number; licenseReg: number; titleFee: number; tempTag: number;
  tradePayoff: number; totalDue: number; cashDown: number;
  deferredDown: number; unpaid: number;
}

export interface FormContext {
  dealer: DealerSettings;
  buyer: Person;
  coBuyer: Person | null;
  vehicle: Vehicle;
  trade: TradeIn | null;
  deal: Deal;
  odometerReadDate: string;     // ISO
  /** 1 = actual, 2 = not actual, 3 = exceeds mechanical limits */
  odometerStatus: 1 | 2 | 3;
  amounts?: DealAmountsCents;
  /** 0 = sold as-is. Drives which Buyers Guide is printed and what it says. */
  warrantyDays?: number;
  /** The mileage half of the same warranty. The Buyers Guide states it in its
      own words; the We Owe needs it so it can say what it is *not* changing. */
  warrantyMiles?: number;
  /** Coverage on the vehicle being sold, not on the person. */
  insurance?: {
    company: string | null;
    policyNo: string | null;
    companyCode: string | null;
  };
  /** The first money taken, if any, so the receipt is not blank where it
      could be filled. Hold period and refundability stay for the counter. */
  deposit?: { amountCents: number; method: string; reference: string | null;
              takenOn: string };
  /** Stock number, which lives on the vehicle rather than the deal. */
  stockNo?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Right-align a number into N single-character boxes:
 * 143568 -> ['1','4','3','5','6','8'].
 *
 * Returns a fixed-length tuple so callers can index it without the compiler
 * insisting each box might be undefined — the length is guaranteed here.
 */
export function digitBoxes(value: number, boxes: 6): [string, string, string, string, string, string];
export function digitBoxes(value: number, boxes: number): string[];
export function digitBoxes(value: number, boxes: number): string[] {
  const s = String(Math.max(0, Math.trunc(value)));
  if (s.length > boxes) {
    throw new Error(`Value ${value} needs ${s.length} boxes but only ${boxes} exist`);
  }
  return s.padStart(boxes, " ").split("").map((c) => (c === " " ? "" : c));
}

/** ISO date -> { mm, dd, yyyy } as zero-padded strings. */
export function dateParts(iso: string): { mm: string; dd: string; yyyy: string } {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) throw new Error(`Not an ISO date: ${iso}`);
  return { yyyy: m[1]!, mm: m[2]!, dd: m[3]! };
}

/** 273300 -> { dollars: "2733", cents: "00" } */
export function splitCents(cents: number): { dollars: string; cents: string } {
  if (!Number.isInteger(cents) || cents < 0) {
    throw new Error(`Bad cents value: ${cents}`);
  }
  return {
    dollars: String(Math.floor(cents / 100)),
    cents: String(cents % 100).padStart(2, "0"),
  };
}

/** 273300 -> "2733.00" for single-field money boxes. */
export function money(cents: number): string {
  const { dollars, cents: c } = splitCents(cents);
  return `${dollars}.${c}`;
}

/** "2395551234" -> "(239) 555-1234" */
export function phone(digits: string | null): string {
  if (!digits || !/^\d{10}$/.test(digits)) return "";
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Name as it appears on the driver license: First Middle Last Suffix. */
export function fullName(p: Person): string {
  return [p.firstName, p.middleName, p.lastName, p.suffix]
    .filter((s) => s && s.trim())
    .join(" ");
}

function mailStreet(p: Person) { return p.mailSameAsPhysical ? p.street : p.mailStreet ?? ""; }
function mailCity(p: Person)   { return p.mailSameAsPhysical ? p.city   : p.mailCity   ?? ""; }
function mailState(p: Person)  { return p.mailSameAsPhysical ? p.state  : p.mailState  ?? ""; }
function mailZip(p: Person)    { return p.mailSameAsPhysical ? p.zip    : p.mailZip    ?? ""; }

export type FieldValues = Record<string, string>;

// ---------------------------------------------------------------------------
// HSMV 82040 — Application for Certificate of Motor Vehicle Title
// ---------------------------------------------------------------------------

export function map82040(ctx: FormContext): FieldValues {
  const { dealer, buyer, coBuyer, vehicle, trade, deal } = ctx;
  const acq = dateParts(deal.agreementDate);
  const read = dateParts(ctx.odometerReadDate);
  const odo = digitBoxes(vehicle.odometer, 6);
  const price = splitCents(deal.cashPriceCents);

  const f: FieldValues = {
    // Header — a used car changing hands is a Transfer, not an Original.
    "Transfer": "/On",

    /* Section 2. A transfer names the plate coming across; if the buyer
       cannot produce the number, the tax collector wants "Tag Credit" in the
       same box. A new plate leaves it empty for them to assign. */
    "License Plate Number":
      deal.tagChoice === "transfer" ? (deal.tagNumber || "Tag Credit") : "",

    // Section 1 — owner
    "Owner s County of Residence": buyer.county ?? "",
    "Are you a Florida Resident": buyer.state === "FL" ? "/YES" : "/NO",
    /* For a business the applicant IS the company: its registered name goes
       in the owner field and its EIN goes where a license number would. The
       person's own details stay on the form as the signer, further down. */
    "Owner’s Name as It Appears on Driver License (First, Full Middle/Maiden, & Last Name)":
      buyer.isBusiness ? (buyer.businessName ?? "") : fullName(buyer),
    "Owner’s Phone Number (Voluntary)": phone(buyer.phoneCell),
    "Owners Email Voluntary": buyer.email ?? "",
    "Owner's Date of Birth": buyer.isBusiness ? "" : usDate(buyer.dateOfBirth),
    "Owner's FL DL/ID or FEID/Suffix Number":
      buyer.isBusiness ? (buyer.businessEin ?? "") : buyer.idNumber,
    "FEID/Suffix #": buyer.isBusiness ? (buyer.businessEin ?? "") : "",
    "Owners Mailing Address": mailStreet(buyer),
    "Owner's City": mailCity(buyer),
    "Owner's State": mailState(buyer),
    "Owner's Zip Code": mailZip(buyer),
    "Owner's Residential Street Address": buyer.street,
    "Owner’s Residential City": buyer.city,
    "Owner’s Residential State": buyer.state,
    "Owner’s Residential Zip Code": buyer.zip,

    // Section 2 — vehicle
    "Vehicle Identification Number VIN": vehicle.vin,
    "Florida Title Number": vehicle.titleNumber ?? "",
    "Previous State of Issue": vehicle.titleState ?? "",
    "Make/Manufacturer": vehicle.make,
    "Model": vehicle.model,
    "Year": String(vehicle.year),
    "Body": vehicle.bodyType ?? "",
    "Color": vehicle.exteriorColor ?? "",
    "Weight": vehicle.weight ?? "",

    // Section 5 — transfer type
    "Sale": "/On",
    "Sale Price in US Dollars": price.dollars,
    "Sale Price in Cents": price.cents,
    "Date Acquired MM": acq.mm,
    "Date Aquired DD": acq.dd,      // sic — misspelled in the PDF
    "Date Aquired YYYY": acq.yyyy,  // sic

    // Section 6 — odometer
    "6-digit odometer now reads": "/On",
    "100, 000 Place": odo[0]!,
    "10,000 Place": odo[1]!,
    "1,000 Place": odo[2]!,
    "100 Place": odo[3]!,
    "10 Place": odo[4]!,
    "Ones Place": odo[5]!,
    "Date Read MM": read.mm,
    "Date Read DD": read.dd,
    "Date Read YYYY": read.yyyy,

    // Section 7 — dealer sales tax report
    "Florida Sales Tax Registration Number": dealer.salesTaxRegNo,
    "Dealer License Number": dealer.flhsmvLicenseNo,
    "Date of Sale": usDate(deal.agreementDate),
    "Amount of Tax": money(deal.stateTaxCents + deal.surtaxCents),

    // Section 8 — VIN verification, certified by the dealer
    "Vehicle Identification Number VIN_2": vehicle.vin,
    /* Left blank for the inspector to write. It is a personal certification
       that a named individual physically checked the VIN, and pre-printing
       the dealership's name claimed a company had done it. */
    "Name Certifying Inspector": "",
    "Florida Dealer": "/On",
    "Dealer Name": dealer.dba ?? dealer.legalName,
    "Dealer Number": dealer.flhsmvLicenseNo,
    "Date of Inspector Signature": usDate(deal.agreementDate),

    // Section 12 — attestation
    /* Section 12 has a date beside each signature and it was never filled,
       leaving the buyer's line undated on every title application. */
    "Date of Applicant, Owner Signature MM/DD/YYYY": usDate(deal.agreementDate),

    /* Section 12. A company cannot sign, so an officer signs their own name
       with their title beside it — that is what binds the company. */
    "Full Name of Applicant Owner": buyer.isBusiness
      ? `${fullName(buyer)}, ${buyer.businessTitle ?? ""}`.replace(/, $/, "")
      : fullName(buyer),
  };

  // Odometer certification — exactly one box.
  f["1  REFLECTS ACTUAL MILEAGE"] = ctx.odometerStatus === 1 ? "/On" : "/Off";
  f["2 IS NOT THE ACTUAL MILEAGE"] = ctx.odometerStatus === 2 ? "/On" : "/Off";
  f["3 IS IN EXCESS OF ITS MECHANICAL LIMITS"] = ctx.odometerStatus === 3 ? "/On" : "/Off";

  // Title brands, if the vehicle carries one.
  const brand = (vehicle.titleBrand ?? "").toLowerCase();
  if (brand.includes("flood")) f["Flood"] = "/On";
  if (brand.includes("rebuilt")) f["Rebuilt"] = "/On";

  if (coBuyer) {
    /* Which box is ticked decides who can sell the car afterwards: AND needs
       both signatures, OR needs either. Leaving both blank sends the tax
       collector guessing, so one is always ticked when there are two names. */
    if (deal.jointOwnership === "or") {
      f["Joint Ownership: OR"] = "/On";
    } else {
      f["Joint Ownership: AND"] = "/On";
    }

    f["Co-Owner Are you a Florida Resident"] = coBuyer.state === "FL" ? "/YES_4" : "/NO_4";
    f["Co-Owner or Lessee’s Name as It Appears on Driver License (First, Full Middle/Maiden, & Last Name)"] =
      fullName(coBuyer);
    f["Co-Owner’s Phone Number (Voluntary)"] = phone(coBuyer.phoneCell);
    f["Co-Owner’s Email (Voluntary)"] = coBuyer.email ?? "";
    f["Co-Owner’s Date of Birth"] = usDate(coBuyer.dateOfBirth);
    f["Date of Applicant, Co-Owner Signature MM/DD/YYYY"] = usDate(deal.agreementDate);
    f["Co-Owner’s FL DL/ID or FEID/Suffix Number"] = coBuyer.idNumber;
    f["Co-Owner’s/Lessee’s Mailing Address"] = mailStreet(coBuyer);
    f["Co-Owner’s/Lessee’s Mailing City"] = mailCity(coBuyer);
    f["Co-Owner’s/Lessee’s Mailing State"] = mailState(coBuyer);
    f["Co-Owner’s/Lessee’s Mailing Zip Code"] = mailZip(coBuyer);
    f["CoOwnersLessees Residential Street Address"] = coBuyer.street;
    f["Co-Owner’s/Lessee’s Residential City"] = coBuyer.city;
    f["Co-Owner’s/Lessee’s Residential State"] = coBuyer.state;
    f["Co-Owner’s/Lessee’s Residential Zip Code"] = coBuyer.zip;
    f["Full Name of Applicant CoOwner"] = fullName(coBuyer);
  }

  if (trade) {
    f["Year of Trade In"] = String(trade.year);
    f["Make of Trade In"] = trade.make;
    f["Title Number of Trade In If known"] = trade.titleNumber ?? "";
    f["Vehicle Identification Number VIN of Trade In"] = trade.vin;
  }

  return f;
}

// ---------------------------------------------------------------------------
// HSMV 82993 — Separate Odometer Disclosure Statement
// ---------------------------------------------------------------------------

export function map82993(ctx: FormContext): FieldValues {
  const { dealer, buyer, vehicle } = ctx;
  const read = dateParts(ctx.odometerReadDate);
  const odo = digitBoxes(vehicle.odometer, 6);

  const f: FieldValues = {
    "Vehicle Identification Number": vehicle.vin,
    "Year": String(vehicle.year),
    // These three are capped at six characters — the state wants its own
    // codes here, not the full brand name.
    "Make": makeCode(vehicle.make),
    "Color": colorCode(vehicle.exteriorColor ?? ""),
    "Body": bodyCode(vehicle.bodyType ?? ""),
    "Title Number": vehicle.titleNumber ?? "",

    "6 DIGIT ODOMETER NOWS READS": "/On",   // sic — "NOWS" in the PDF
    "Odo1": odo[0]!, "Odo2": odo[1]!, "Odo3": odo[2]!,
    "Odo4": odo[3]!, "Odo5": odo[4]!, "Odo6": odo[5]!,
    "month": read.mm, "day": read.dd, "year": read.yyyy,

    // The dealer is the seller here.
    "Sellers Printed Name": dealer.dba ?? dealer.legalName,
    "Sellers Street Address": dealer.street,
    "City": dealer.city,
    "State": dealer.state,
    "Zip": dealer.zip,

    "Buyers Printed Name": fullName(buyer),
    "Buyers Street Address": buyer.street,
    "City_2": buyer.city,
    "State_2": buyer.state,
    "Zip_2": buyer.zip,
  };

  f["1 REFLECTS ACTUAL MILEAGE"] = ctx.odometerStatus === 1 ? "/On" : "/Off";
  f["2 IS IN EXCESS OF ITS MECHANICAL LIMITSEXCESS OF ITS MECHANICAL LIMITS APPLIES TO 5 DIGIT ODOMETERS"] =
    ctx.odometerStatus === 3 ? "/On" : "/Off";
  f["3 IS NOT THE ACTUAL MILEAGE  WARNING  ODOMETER DISCREPANCY"] =
    ctx.odometerStatus === 2 ? "/On" : "/Off";

  return f;
}

// ---------------------------------------------------------------------------
// HSMV 83330 — Florida Insurance Affidavit
// ---------------------------------------------------------------------------

export function map83330(ctx: FormContext): FieldValues {
  const { buyer, vehicle, insurance } = ctx;
  return {
    "Name of Insured": fullName(buyer),
    "Name of Insurance Company": insurance?.company ?? "",
    "Policy Number": insurance?.policyNo ?? "",
    "Company Code Number 5 digits": insurance?.companyCode ?? "",
    "Year": String(vehicle.year),
    "Make": makeCode(vehicle.make),
    "Vehicle Identification Number": vehicle.vin,
  };
}

// ---------------------------------------------------------------------------
// FTC Buyers Guide (English) — 16 CFR Part 455
// ---------------------------------------------------------------------------
// The PDF holds two mutually exclusive variants. Page 1 is the "AS IS" version
// used in states that permit as-is sales (Florida does). Page 2 is the
// "IMPLIED WARRANTIES ONLY" version for states that do not. Page 3 is the
// shared back page. Keep pages 1 and 3; drop page 2 — see BUYERS_GUIDE_PAGES.

export const BUYERS_GUIDE_PAGES = { asIs: 0, impliedWarranty: 1, back: 2 } as const;
export const BUYERS_GUIDE_KEEP = [BUYERS_GUIDE_PAGES.asIs, BUYERS_GUIDE_PAGES.back];

const BG = "topmostSubform[0].BG-AsIs[0]";
const BGB = "topmostSubform[0].BG-Back[0]";

export function mapBuyersGuideEn(ctx: FormContext): FieldValues {
  const { dealer, vehicle } = ctx;
  return {
    [`${BG}.VehicleMake[0]`]: vehicle.make,
    [`${BG}.Model[0]`]: vehicle.model,
    [`${BG}.Year[0]`]: String(vehicle.year),
    [`${BG}.VIN[0]`]: vehicle.vin,
    [`${BG}.Warranty[0]`]: "/As Is",
    [`${BG}.ServiceContract[0]`]: "/Off",   // no service contracts sold

    [`${BGB}.DealerName[0]`]: dealer.dba ?? dealer.legalName,
    [`${BGB}.DealerAddress[0]`]: `${dealer.street}, ${dealer.city}, ${dealer.state} ${dealer.zip}`,
    [`${BGB}.DealerPhone[0]`]: phone(dealer.phone),
    [`${BGB}.DealerEmail[0]`]: dealer.email,
    [`${BGB}.ComplaintContact[0]`]: dealer.dba ?? dealer.legalName,
  };
}

// ---------------------------------------------------------------------------
// Purchase agreement (buyer's order)
// ---------------------------------------------------------------------------
// Now a proper fillable PDF, so this is field mapping rather than drawing at
// measured coordinates. Blank rows stay blank instead of printing $0.00.

const zeroBlank = (cents: number | undefined) =>
  !cents ? "" : money(cents);

export function mapPurchaseAgreement(ctx: FormContext): FieldValues {
  const { dealer, buyer, coBuyer, vehicle, trade, deal } = ctx;
  const a = ctx.amounts;

  const addr = (p: Person) =>
    [p.city, p.county, p.state].filter(Boolean).join(", ") + ` ${p.zip}`;

  const f: FieldValues = {
    deal_number: deal.dealNo,
    agreement_date: usDate(deal.agreementDate),
    /* Each signature line has a date beside it; leaving them blank means
       someone writes them by hand, in whatever format comes to mind. */
    buyer_sig_date: usDate(deal.agreementDate),
    cobuyer_sig_date: ctx.coBuyer ? usDate(deal.agreementDate) : "",
    dealer_sig_date: usDate(deal.agreementDate),

    dealer_name: dealer.dba ?? dealer.legalName,
    dealer_license: dealer.flhsmvLicenseNo,
    dealer_street: dealer.street,
    dealer_csz: `${dealer.city}, ${dealer.state} ${dealer.zip}`,
    dealer_phone: phone(dealer.phone),
    dealer_email: dealer.email,

    buyer_name: fullName(buyer),
    buyer_street: buyer.street,
    buyer_csz: addr(buyer),
    buyer_cell: phone(buyer.phoneCell),
    buyer_email: buyer.email ?? "",

    condition: "/used",

    stock_no: ctx.stockNo ?? "",
    mileage: vehicle.odometer.toLocaleString("en-US"),
    veh_year: String(vehicle.year),
    veh_make: vehicle.make,
    veh_model: vehicle.model,
    veh_color: vehicle.exteriorColor ?? "",
    veh_body: vehicle.bodyType ?? "",
    veh_vin: vehicle.vin,
    veh_title_state: vehicle.titleState ?? "FL",
    veh_title_status: vehicle.titleBrand ?? "Clean",

    buyer_printed: fullName(buyer),
    dealer_printed: dealer.dba ?? dealer.legalName,
  };

  if (coBuyer) {
    f.cobuyer_name = fullName(coBuyer);
    f.cobuyer_street = coBuyer.street;
    f.cobuyer_csz = addr(coBuyer);
    f.cobuyer_cell = phone(coBuyer.phoneCell);
    f.cobuyer_email = coBuyer.email ?? "";
    f.cobuyer_printed = fullName(coBuyer);
  }

  if (a) {
    f.cash_price = money(a.cashPrice);
    f.predelivery = zeroBlank(a.predelivery);
    f.efs_fee = zeroBlank(a.efiling);
    f.trade_allowance = zeroBlank(a.tradeAllowance);
    f.taxable_amount = money(a.taxable);
    f.sales_tax = money(a.stateTax);
    f.surtax = money(a.surtax);
    f.total_sales_price = money(a.totalSales);
    f.license_fees = zeroBlank(a.licenseReg);
    f.title_fee = zeroBlank(a.titleFee);
    f.temp_tag_fee = zeroBlank(a.tempTag);
    f.trade_payoff = zeroBlank(a.tradePayoff);
    f.total_amount_due = money(a.totalDue);
    f.cash_down = zeroBlank(a.cashDown);
    f.deferred_down = zeroBlank(a.deferredDown);
    f.unpaid_balance = money(a.unpaid);
  }

  if (trade) {
    f.ti_year = String(trade.year);
    f.ti_make = trade.make;
    f.ti_vin = trade.vin;
    f.ti_brand_none = "/Yes";
  }

  return f;
}

// ---------------------------------------------------------------------------
// Vehicle deposit receipt
// ---------------------------------------------------------------------------
// Taken before there is a binding contract, so the amount, hold period and
// refundability are left blank on purpose — those are agreed at the counter
// and § 501.976(10) requires them to be stated, not defaulted.

const METHOD_LABEL: Record<string, string> = {
  cash: "Cash", card: "Card", check: "Check", ach: "ACH",
  wire: "Wire", lender_check: "Lender check", other: "Other",
};

export function mapDepositReceipt(ctx: FormContext): FieldValues {
  const { dealer, buyer, vehicle, deposit } = ctx;
  const f: FieldValues = {
    // Derived from the deal number so the receipt and the deal are traceable
    // to each other without a second sequence to keep in step.
    receipt_no: `R-${ctx.deal.dealNo.replace(/^D-/, "")}`,
    receipt_date: usDate(deposit?.takenOn ?? new Date().toISOString().slice(0, 10)),
    customer_sig_date: usDate(deposit?.takenOn ?? new Date().toISOString().slice(0, 10)),
    dealer_sig_date: usDate(deposit?.takenOn ?? new Date().toISOString().slice(0, 10)),
    applied_to_deal_no: ctx.deal.dealNo,
    asking_price: vehicle.askingPriceCents ? money(vehicle.askingPriceCents) : "",

    dealer_name: dealer.dba ?? dealer.legalName,
    dealer_license: dealer.flhsmvLicenseNo,
    dealer_street: dealer.street,
    dealer_csz: `${dealer.city}, ${dealer.state} ${dealer.zip}`,
    dealer_phone: phone(dealer.phone),
    dealer_email: dealer.email,

    customer_name: fullName(buyer),
    customer_phone: phone(buyer.phoneCell),
    customer_email: buyer.email ?? "",
    customer_street: buyer.street,
    customer_csz: `${buyer.city}, ${buyer.state} ${buyer.zip}`,

    veh_year: String(vehicle.year),
    veh_make: vehicle.make,
    veh_model: vehicle.model,
    veh_color: vehicle.exteriorColor ?? "",
    stock_no: ctx.stockNo ?? "",
    veh_vin: vehicle.vin,
    mileage: vehicle.odometer.toLocaleString("en-US"),

    customer_printed: fullName(buyer),
    dealer_printed: dealer.dba ?? dealer.legalName,
  };

  // Fill the money only if it has actually been taken. Hold period and
  // refundability stay blank: § 501.976(10) requires those to be agreed and
  // written down, and software should not invent them.
  if (deposit) {
    f.deposit_amount = money(deposit.amountCents);
    f.deposit_method = METHOD_LABEL[deposit.method] ?? deposit.method;
    f.deposit_ref = deposit.reference ?? "";
  }

  return f;
}

// ---------------------------------------------------------------------------
// Buyers Guide — warranty version
// ---------------------------------------------------------------------------
// The dealer's own guide, used when an in-house warranty is included. The
// warranty boxes and coverage text are printed into the page, so only the
// vehicle and dealer details are filled here.

export function mapBuyersGuideWarranty(ctx: FormContext): FieldValues {
  const { dealer, vehicle } = ctx;
  return {
    systems_covered_duration: warrantySummary(ctx.warrantyDays ?? 0),
    vehicle_make: vehicle.make,
    vehicle_model: vehicle.model,
    vehicle_year: String(vehicle.year),
    vehicle_vin: vehicle.vin,
    dealer_address: `${dealer.street}, ${dealer.city}, ${dealer.state} ${dealer.zip}`,
    dealer_phone: phone(dealer.phone),
    dealer_email: dealer.email,
    complaints_contact: dealer.dba ?? dealer.legalName,
  };
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

export interface FormSpec {
  key: string;
  title: string;
  templatePath: string;
  fillMethod: "acroform" | "coordinate_overlay";
  /** Zero-based pages to keep, or undefined for all. */
  keepPages?: number[];
  /** Template has an XFA layer that must be stripped before filling. */
  hasXfa?: boolean;
  /** Point size used when wrapping multiline fields. Matches the template's DA. */
  multilineSize?: number;
  map?: (ctx: FormContext) => FieldValues;
}

export const FORMS: FormSpec[] = [
  { key: "purchase_agreement", title: "Used Vehicle Purchase Agreement",
    templatePath: "/forms/purchase_agreement.pdf", fillMethod: "acroform",
    map: mapPurchaseAgreement },
  { key: "hsmv_82040", title: "Application for Certificate of Motor Vehicle Title",
    templatePath: "/forms/82040.pdf", fillMethod: "acroform", map: map82040 },
  { key: "hsmv_82993", title: "Separate Odometer Disclosure Statement",
    templatePath: "/forms/82993.pdf", fillMethod: "acroform", map: map82993 },
  { key: "hsmv_83330", title: "Florida Insurance Affidavit",
    templatePath: "/forms/83330.pdf", fillMethod: "acroform", map: map83330 },
  { key: "buyers_guide_en", title: "FTC Buyers Guide (English)",
    templatePath: "/forms/buyers_guide_en.pdf", fillMethod: "acroform",
    keepPages: BUYERS_GUIDE_KEEP, hasXfa: true, map: mapBuyersGuideEn },
  { key: "buyers_guide_es", title: "Guía del Comprador (Español)",
    templatePath: "/forms/buyers_guide_es.pdf", fillMethod: "coordinate_overlay" },
  { key: "buyers_guide_warranty", title: "Buyers Guide (with warranty)",
    templatePath: "/forms/buyers_guide_warranty.pdf", fillMethod: "acroform",
    multilineSize: 8, map: mapBuyersGuideWarranty },
  { key: "deposit_receipt", title: "Vehicle Deposit Receipt",
    templatePath: "/forms/deposit_receipt.pdf", fillMethod: "acroform",
    map: mapDepositReceipt },
];

/** Order of the merged print bundle for a retail sale. */
export const RETAIL_PACKET = [
  "purchase_agreement",
  "hsmv_82040",
  "hsmv_82993",
  "hsmv_83330",
  "buyers_guide_en",
];
